import {
  createLightTheme,
  createTeamsDarkTheme,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  type BrandVariants,
  type Theme,
} from '@fluentui/react-components';

/**
 * The app's brand ramp and the three themes built from it (#202).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE ONLY FILE IN `apps/web/src` ALLOWED TO CONTAIN HEX LITERALS.
 * ─────────────────────────────────────────────────────────────────────────────
 * CLAUDE.md's "tokens only, no hex" rule exists so a colour cannot be written
 * that fails to follow the theme. A `BrandVariants` ramp is the one input the
 * theme is *built from*, so it has to be literal — there is no token to
 * reference yet. `styles/noRawStyleValues.test.ts` allowlists exactly this
 * path and fails on a hex anywhere else, so the exception stays one file wide.
 *
 * Everything downstream still reads `tokens.*`. `tokens` is
 * `Record<keyof Theme, string>` of literal `var(--<key>)` references, and the
 * key set is identical across all three themes (measured: 459 keys for
 * `createLightTheme(prBrand)`, `createTeamsDarkTheme(prBrand)`,
 * `createHighContrastTheme()` and `teamsLightTheme` alike — zero missing, zero
 * extra), so every existing `makeStyles` rule picks the new brand up with no
 * change at all.
 */

/**
 * The 16-stop indigo ramp.
 *
 * `BrandVariants` is `Record<Brands, string>` where `Brands` is the literal
 * union `10 | 20 | … | 160` — sixteen numeric keys, all required. `Record`
 * makes a missing stop a compile error, so this cannot silently go short.
 *
 * Built from an (L*, chroma) schedule at a fixed CIE-Lab hue of 296.75°, which
 * is the exact Lab hue of the approved accent #3D4FC4 and within 0.3° of
 * Fluent's own `brandTeams` (297.01°) — so it reads as a sibling of the Teams
 * default rather than as a foreign colour sitting next to Teams' chrome.
 *
 * L* increases monotonically from 10 → 160, matching the direction of all four
 * ramps Fluent ships. That direction is load-bearing rather than cosmetic: the
 * light theme's hover is DARKER than rest (brand70 vs brand80) while Teams
 * dark's hover is BRIGHTER than rest (brand80 vs brand70), so a non-monotonic
 * ramp inverts the hover state in one theme or the other.
 *
 * The approved #3D4FC4 lands on stop 70 (#3b4ec3 — ΔE76 0.46, well inside the
 * ~2.3 just-noticeable difference), NOT on stop 80. That was a measured trade,
 * not a slip: dark's focus ring draws `colorStrokeFocus1` (#000000) directly on
 * the brand70 button fill, and contrast(#000000, X) = (Y + 0.05) / 0.05 needs
 * L* ≥ 37.84 to reach 3:1. Anchoring the accent at stop 80 pushed stop 70 down
 * to L* 35.5 → 2.74:1, a real WCAG 1.4.11 failure on the focus indicator.
 * Anchoring at 70 gives 3.06:1 — PASS. Stop 80 then lands on #525ac7, and the
 * light primary button with white text measures 5.76:1, better than the 5.38:1
 * of the Teams default it replaces.
 *
 * No stop needed sRGB gamut clamping. The smallest adjacent ΔE76 is 5.61, and
 * the tightest interactive pair (70 → 80, light rest → hover) is 7.57, so every
 * hover and pressed state is a visible change rather than a theoretical one.
 */
export const prBrand: BrandVariants = {
  10: '#2c2a4a',
  20: '#302f57',
  30: '#343365',
  40: '#373977',
  50: '#3b3f8b',
  60: '#3e47a4',
  70: '#3b4ec3', // the approved accent — light link, DARK primary button
  80: '#525ac7', // LIGHT primary button, colorBrandForeground1, brand strokes
  90: '#7276eb',
  100: '#8c8af5', // dark colorBrandForeground1 / link
  110: '#a09bfa',
  120: '#b6affb',
  130: '#c2bbfb',
  140: '#d1cbfc',
  150: '#e2def9',
  160: '#edebfa',
};

/**
 * LIGHT.
 *
 * `teamsLightTheme` is exactly `{ ...createLightTheme(brandTeams),
 * ...teamsFontFamilies }` — diffing `createLightTheme(<any ramp>)` against it
 * across all 459 keys yields exactly two differences that are not brand-derived,
 * and one of those two (`colorNeutralStrokeAccessibleSelected`) IS brand-derived,
 * its name simply lacks "Brand". So there is no separate Teams neutral palette
 * in light: the only thing that must be carried across is the font stack.
 *
 * Spreading `teamsLightTheme` first and the generated theme second is a no-op
 * today and guards against a Teams-only light alias file appearing in a future
 * `@fluentui/tokens`. The explicit `fontFamilyBase` restore is REQUIRED — the
 * second spread clobbers it, and the two stacks genuinely differ:
 *
 *   teams:   '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, …'
 *   created: "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, …"
 *
 * Without the restore the tab silently drifts off the Teams type stack, which
 * is the sort of thing nobody notices until a screenshot is compared.
 *
 * The spread order is easy to reverse and fails SILENTLY —
 * `{ ...createLightTheme(prBrand), ...teamsLightTheme }` compiles fine and
 * reverts the brand entirely. `brand.test.ts` asserts the brand actually landed.
 */
export const prLightTheme: Theme = {
  ...teamsLightTheme,
  ...createLightTheme(prBrand),
  fontFamilyBase: teamsLightTheme.fontFamilyBase,
};

/**
 * DARK — must use `createTeamsDarkTheme`, never `createDarkTheme`.
 *
 * The two factories differ in 20 of 459 keys. Seventeen are neutral
 * BACKGROUNDS — `colorNeutralBackground2`…`5` and every Hover/Pressed/Selected
 * variant — where Teams dark sits one grey step lighter than web dark
 * (`colorNeutralBackground2` is #242424 vs #1f1f1f). The other three are brand
 * remappings. Using `createDarkTheme` here would quietly swap the Teams neutral
 * palette this app is required to keep.
 *
 * ONE design-layer override, and it is measured rather than taste:
 * `colorBrandForegroundLinkPressed` defaults to brand[90] (#7276eb), which is
 * 3.81:1 on `colorNeutralBackground1` (#292929) — under AA-normal's 4.5:1.
 * Fluent's own brandTeams fails the same pair at 3.94:1, so it is inherited,
 * not caused by this ramp. It cannot be fixed in the ramp: lifting stop 90 to
 * clear 4.5 needs L* ≈ 59.5, which is stop 100's value, collapsing the pressed
 * and default link into one colour. Overriding this single role to brand[110]
 * (#a09bfa) measures 5.93:1 and leaves brand[90] free for
 * `colorCompoundBrandStroke`, where the non-text 3:1 rule applies and 3.81 passes.
 */
export const prDarkTheme: Theme = {
  ...teamsDarkTheme,
  ...createTeamsDarkTheme(prBrand),
  fontFamilyBase: teamsDarkTheme.fontFamilyBase,
  colorBrandForegroundLinkPressed: prBrand[110],
};

/**
 * HIGH CONTRAST — the stock theme, passed through untouched.
 *
 * `createHighContrastTheme` takes no brand argument at all, and every one of
 * the 49 brand-derived tokens is forced onto a Windows system colour
 * (measured: `colorBrandBackground` #ffffff, `colorBrandForegroundLink`
 * #ffff00, `colorCompoundBrandStroke` #1aebff). That collapse is the entire
 * point of the mode — hue is not an available channel — so reintroducing indigo
 * would either be invisible on pure black or defeat the mandated system-colour
 * contrast.
 *
 * This is also the justification for the "never colour alone" rules the rest of
 * the design system enforces: radius, spacing and shadow tokens are
 * theme-invariant, so the system's structure survives high contrast intact.
 * Only the hue disappears — which is why every selected, active, sorted and
 * status state must carry a glyph, a word or a border-width change too.
 */
export const prHighContrastTheme: Theme = teamsHighContrastTheme;
